import React, { useState, useEffect } from 'react';

interface NavbarProps {
  onAnchor: (anchor: string) => void;
  onTestimonials?: () => void;
  onPhotos: () => void;
  onLogin: () => void;
  activeSection: string;
}

const Navbar = ({ onAnchor, onTestimonials, onPhotos, onLogin, activeSection }: NavbarProps) => {
  const [open, setOpen] = useState(false);

  const desktopLinkClass = (section: string) =>
    `flex h-full items-center border-b-2 px-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mainBlue ${
      activeSection === section
        ? 'border-mainPink font-semibold text-mainPink'
        : 'border-transparent text-gray-800 hover:text-mainBlue'
    }`;
  const mobileLinkClass = (section: string) =>
    `min-h-[48px] rounded px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mainBlue ${
      activeSection === section
        ? 'bg-mainPink font-semibold text-white'
        : 'text-gray-900 hover:bg-gray-50'
    }`;

  // Navigate to home route, then scroll to the given section anchor.
  const goHome = (anchor: string) => {
    setOpen(false);
    if (anchor === 'testimonials' && onTestimonials) onTestimonials();
    else onAnchor(anchor);
  };

  // Keep the previous scroll-lock state so other overlays are not affected.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  return (
    <>
      <nav className="fixed top-0 w-full bg-white shadow-md h-20 px-4 md:px-6 flex items-center z-40 animate-slide-down">
        <button
          onClick={() => goHome('home')}
          aria-label="ETA Learning — Go to homepage"
          className="flex items-center gap-3 h-full min-h-[44px] focus:outline-none rounded cursor-pointer"
        >
          <img
            src={`${process.env.PUBLIC_URL}/Short.svg`}
            alt="ETA logo"
            className="h-12 sm:h-14 xl:h-16 w-auto"
          />
          <span className="sr-only">ETA Learning</span>
        </button>

        {/* Desktop links */}
        <div className="hidden xl:flex items-center gap-4 ml-auto h-full">
          <button type="button" onClick={() => goHome('home')} aria-current={activeSection === 'home' ? 'location' : undefined} className={desktopLinkClass('home')}>Home</button>
          <button type="button" onClick={() => goHome('programs')} aria-current={activeSection === 'programs' ? 'location' : undefined} className={desktopLinkClass('programs')}>Programs + Plans</button>
          <button type="button" onClick={() => onPhotos()} aria-current={activeSection === 'photos' ? 'page' : undefined} className={desktopLinkClass('photos')}>Learning Photos</button>
          <button type="button" onClick={() => goHome('testimonials')} aria-current={activeSection === 'testimonials' ? 'location' : undefined} className={desktopLinkClass('testimonials')}>Testimonials</button>
          <button type="button" onClick={() => goHome('faq')} aria-current={activeSection === 'faq' ? 'location' : undefined} className={desktopLinkClass('faq')}>FAQ</button>
          <button type="button" onClick={() => goHome('contact')} aria-current={activeSection === 'contact' ? 'location' : undefined} className={`flex items-center rounded px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mainBlue ${activeSection === 'contact' ? 'bg-mainPink font-semibold text-white ring-2 ring-mainPink ring-offset-2' : 'bg-mainPink text-white hover:bg-mainBlue'}`}>Contact</button>
          <button type="button" onClick={() => { setOpen(false); onLogin(); }} aria-current={activeSection === 'login' ? 'page' : undefined} className={`rounded border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mainBlue ${activeSection === 'login' ? 'border-mainPink bg-mainPink font-semibold text-white' : 'text-gray-700 hover:bg-gray-50'}`}>Login</button>
        </div>

        {/* Mobile hamburger */}
        <div className="xl:hidden bg-white ml-auto">
          <button
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="mobile-navigation"
            onClick={() => setOpen((s) => !s)}
            className="min-h-[44px] min-w-[44px] rounded p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-mainBlue"
          >
            {open ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay — sibling of <nav>, not a child, so it can use full viewport positioning */}
      {open && (
        <div id="mobile-navigation" className="xl:hidden fixed inset-0 z-30 bg-white overflow-y-auto pt-20">
          <div className="p-6">
            {/* Header row – logo removed, keep Close button only */}
            <div className="flex items-center justify-end mb-6">
              <button
                onClick={() => setOpen(false)}
                className="min-h-[44px] px-3 py-2 text-sm text-gray-600 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mainBlue"
              >
                Close
              </button>
            </div>

            <nav className="flex flex-col gap-1 text-lg text-gray-900">
              <button type="button" onClick={() => goHome('home')} aria-current={activeSection === 'home' ? 'location' : undefined} className={mobileLinkClass('home')}>Home</button>
              <button type="button" onClick={() => goHome('programs')} aria-current={activeSection === 'programs' ? 'location' : undefined} className={mobileLinkClass('programs')}>Programs + Plans</button>
              <button type="button" onClick={() => { setOpen(false); onPhotos(); }} aria-current={activeSection === 'photos' ? 'page' : undefined} className={mobileLinkClass('photos')}>Learning Photos</button>
              <button type="button" onClick={() => goHome('testimonials')} aria-current={activeSection === 'testimonials' ? 'location' : undefined} className={mobileLinkClass('testimonials')}>Testimonials</button>
              <button type="button" onClick={() => goHome('faq')} aria-current={activeSection === 'faq' ? 'location' : undefined} className={mobileLinkClass('faq')}>FAQ</button>
              <button type="button" onClick={() => goHome('contact')} aria-current={activeSection === 'contact' ? 'location' : undefined} className={`min-h-[48px] rounded border px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mainBlue ${activeSection === 'contact' ? 'border-mainPink bg-mainPink font-semibold text-white' : 'border-mainPink bg-white text-mainPink hover:bg-mainPink/10'}`}>
                Contact
              </button>

              <div className="border-t mt-4 pt-4">
                <button
                  onClick={() => { setOpen(false); onLogin(); }}
                  aria-current={activeSection === 'login' ? 'page' : undefined}
                  className="min-h-[48px] w-full rounded border px-3 py-3 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mainBlue"
                >
                  Login
                </button>
              </div>

              <div className="mt-6 text-sm text-gray-400">© ETA Learning</div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
